import React, { useContext, useEffect, useReducer, useState } from 'react';
import EditTaskDialogView from '../view/EditTaskDialogView';
import formatDate from '../../../utils/formatDate';
import { FetchCallbacksContext } from '../../../internal-components/FetchCallbacks';
import { formatFileSize } from '../../form/engine/FileUploadFieldEngine';
import useGlobalVariables from '../../../hooks/useGlobalVariables';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const applyProgress = (state, action) =>
  state.map(file => {
    if (file.id === action.id) {
      file.progress = action.progress;
    }

    return file;
  });

const applyComplete = (state, action) =>
  state.map(file => {
    if (file.id === action.id) {
      file.complete = true;
    }

    return file;
  });

const reducer = (state, action) => {
  const { type } = action;

  switch (type) {
    case 'reload':
      return [...action.files];

    case 'drop':
      return [...state, ...action.files];

    case 'delete':
      return [...state.filter(f => f.id !== action.id)];

    case 'progress':
      return [...applyProgress(state, action)];

    case 'complete':
      return [...applyComplete(state, action)];

    default:
      return state;
  }
};

const EditTaskDialogEngine = ({
  canReassign = false,
  closeEditTaskDialog = () => {},
  editedTaskCallback = () => {},
  endUserDialog = false,
  taskBeingEdited = {},
  open,
  viewOnly = false
}) => {
  const { isLoading, setIsLoading } = useGlobalVariables();
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const taskId = taskBeingEdited?.id || null;
  const [isEditing, setIsEditing] = useState(null);
  const [existingFiles, dispatch] = useReducer(reducer, []);
  const timelineReadyCallback = () => {
    setIsLoading(false);
  };
  const handleClose = () => {
    closeEditTaskDialog(null);
  };
  const handleConfirm = () => {
    closeEditTaskDialog();
  };
  const formattedDueDate = taskBeingEdited?.dueDate
    ? formatDate(+taskBeingEdited?.dueDate, false, 'DD/MM/YYYY', true)
    : '';

  const createdDate = taskBeingEdited?.metadata?.createdDate || null;

  const assigneeDisplay = (
    <>
      {taskBeingEdited?.assigneeName}
      {taskBeingEdited?.assigneeEmailAddress ? (
        <>
          <br />
          {taskBeingEdited.assigneeEmailAddress}
        </>
      ) : (
        ''
      )}
    </>
  );

  const authorDisplay = (
    <>
      {taskBeingEdited?.authorName}
      {taskBeingEdited?.authorEmailAddress ? (
        <>
          <br />
          {taskBeingEdited.authorEmailAddress}
        </>
      ) : (
        ''
      )}
    </>
  );

  const setFieldValue = (fieldId, value) => {
    if (!taskId) {
      return;
    }
    let endpoint;
    setIsLoading(true);
    if (fieldId === 'status') {
      endpoint = endUserDialog
        ? `/tasks/v1/eu/tasks/${taskId}/status`
        : `/tasks/v1/tasks/${taskId}/status`;
      fetchAPI(endpoint, {
        method: 'PUT',
        body: {
          status: value
        }
      })
        .then(() => {
          openSnackbar('success', 'snackbars.tasks.edit_success');
          editedTaskCallback({ ...taskBeingEdited, status: value });
        })
        .catch(() => {
          setIsLoading(false);
          openSnackbar('warning', 'snackbars.tasks.edit_failure');
        });
    } else if (fieldId === 'assigneeId') {
      endpoint = endUserDialog ? `/tasks/v1/eu/tasks/${taskId}/assignee` : `/tasks/v1/tasks/${taskId}/assignee`;
      fetchAPI(endpoint, {
        method: 'PUT',
        body: {
          assigneeId: value
        }
      })
        .then(() => {
          openSnackbar('success', 'snackbars.tasks.edit_success');
          editedTaskCallback({ ...taskBeingEdited, assigneeId: value }, true);
        })
        .catch(e => {
          setIsLoading(false);
          openSnackbar('warning', 'snackbars.tasks.edit_failure');
        });
    } else {
      const modifiedTask = { ...taskBeingEdited, [fieldId]: value };
      const modifiedTaskCopy = { ...modifiedTask };
      delete modifiedTask.assigneeEmailAddress;
      delete modifiedTask.assigneeName;
      delete modifiedTask.authorEmailAddress;
      delete modifiedTask.authorName;
      fetchAPI(`/tasks/v1/tasks/${taskId}`, {
        method: 'PUT',
        body: modifiedTask
      })
        .then(() => {
          editedTaskCallback(modifiedTaskCopy);
          openSnackbar('success', 'snackbars.tasks.edit_success');
        })
        .catch(() => {
          setIsLoading(false);
          openSnackbar('warning', 'snackbars.tasks.edit_failure');
        });
    }
  };

  const onAddedComment = () => {
    editedTaskCallback({ ...taskBeingEdited });
  };

  const refreshTimeline = () => {
    editedTaskCallback({ ...taskBeingEdited });
  };

  const onFileDropped = file => {
    if (file.size > MAX_FILE_SIZE * 1.02) {
      openSnackbar(
        'error',
        t('forms.file_upload.too_big', {
          size: formatFileSize(file.size),
          max: formatFileSize(MAX_FILE_SIZE).replace(/\.0\s/, '\xa0')
        })
      );
    } else {
      const id = encodeURIComponent(file.name);
      file.id = id;
      dispatch({ type: 'drop', files: [file] });
      const formData = new FormData();
      formData.append('file', file, id);
      fetchAPI(`/tasks/v1/eu/tasks/${taskId}/upload`, {
        method: 'PUT',
        body: formData,
        onUploadProgress: e => {
          const progress = (e.loaded / e.total) * 100;
          dispatch({
            type: 'progress',
            id,
            progress
          });
        }
      }).then(() => {
        dispatch({ type: 'complete', id });
        editedTaskCallback({ ...taskBeingEdited });
      });
    }
  };

  useEffect(() => {
    if (open) {
      dispatch({ type: 'reload', files: [] });
    }
  }, [open]);

  const viewProps = {
    ...taskBeingEdited,
    canReassign,
    createdDate,
    endUserDialog,
    existingFiles,
    formattedDueDate,
    handleClose,
    handleConfirm,
    isEditing,
    isLoading,
    refreshTimeline,
    setFieldValue,
    setIsEditing,
    taskId,
    timelineReadyCallback,
    onAddedComment,
    onFileDropped,
    open,
    assigneeDisplay,
    authorDisplay,
    viewOnly
  };

  return <EditTaskDialogView {...viewProps} />;
};

export default EditTaskDialogEngine;
